import ArchiveIcon from '@material-design-icons/svg/filled/archive.svg?react';
import EditIcon from '@material-design-icons/svg/filled/edit.svg?react';
import PublishIcon from '@material-design-icons/svg/filled/publish.svg?react';
import SaveIcon from '@material-design-icons/svg/filled/save.svg?react';
import ScienceIcon from '@material-design-icons/svg/filled/science.svg?react';
import UnarchiveIcon from '@material-design-icons/svg/filled/unarchive.svg?react';

import { Button } from '../../../components/ui/button';
import type { SpotlightStatus } from '../types';
import { LifecycleStepper } from './LifecycleStepper';
import { SpotlightStatusBadge } from './StatusBadges';

const MIN_WHITELIST = 4;

interface LifecycleHeaderProps {
  status: SpotlightStatus | null;
  weekId: string;
  themeTitle: string;
  appCount: number;
  isDirty: boolean;
  isSaving: boolean;
  isEditingPublished: boolean;
  onSave: () => void;
  onPublish: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onRunExperiment: () => void;
  onEditPublished: () => void;
  onCancelEdit: () => void;
  onUpdateLive: () => void;
}

export function LifecycleHeader({
  status,
  weekId,
  themeTitle,
  appCount,
  isDirty,
  isSaving,
  isEditingPublished,
  onSave,
  onPublish,
  onArchive,
  onUnarchive,
  onRunExperiment,
  onEditPublished,
  onCancelEdit,
  onUpdateLive,
}: LifecycleHeaderProps) {
  const isNew = status === null;
  const isEditable = isNew || status === 'draft';
  const isPublished = status === 'published';
  const isArchived = status === 'archived';
  const hasId = !isNew;
  const canRunExperiment = hasId && appCount >= MIN_WHITELIST;

  const hint = (() => {
    if (isPublished && isEditingPublished) {
      return isDirty
        ? 'Changes go live to users as soon as you update.'
        : 'Editing the live spotlight.';
    }
    if (isPublished) {
      return 'Live to users — Edit to change what is shown.';
    }
    if (isEditable && isDirty) {
      return 'Unsaved changes — save before publishing.';
    }
    return null;
  })();

  const runExperimentButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={onRunExperiment}
      disabled={!canRunExperiment}
    >
      <ScienceIcon className="size-4 fill-current" />
      Run experiment
    </Button>
  );

  const archiveButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={onArchive}
      className="border-orange-900/50 text-orange-400 hover:border-orange-800/50 hover:bg-orange-950/30"
    >
      <ArchiveIcon className="size-4 fill-current" />
      Archive
    </Button>
  );

  const unarchiveButton = (
    <Button variant="outline" size="sm" onClick={onUnarchive}>
      <UnarchiveIcon className="size-4 fill-current" />
      Unarchive
    </Button>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-lg font-semibold text-white">
              {themeTitle.trim() || (isNew ? 'New spotlight' : 'Untitled')}
            </h2>
            {status && <SpotlightStatusBadge status={status} />}
          </div>
          <p className="mt-0.5 text-sm text-neutral-500">
            {weekId.trim() ? `Week ${weekId}` : 'Set a week ID'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isPublished && isEditingPublished ? (
            <>
              <Button
                onClick={onUpdateLive}
                disabled={isSaving || !isDirty}
                size="sm"
                className="bg-teal-600 text-white hover:bg-teal-500"
              >
                <PublishIcon className="size-4 fill-current" />
                {isSaving ? 'Updating…' : 'Update live'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </>
          ) : isPublished ? (
            <>
              <Button variant="outline" size="sm" onClick={onEditPublished}>
                <EditIcon className="size-4 fill-current" />
                Edit
              </Button>
              {runExperimentButton}
              {archiveButton}
            </>
          ) : isArchived ? (
            <>{unarchiveButton}</>
          ) : (
            <>
              {isEditable && (
                <Button
                  onClick={onSave}
                  disabled={isSaving || (!isNew && !isDirty)}
                  size="sm"
                  className="bg-teal-600 text-white hover:bg-teal-500"
                >
                  <SaveIcon className="size-4 fill-current" />
                  {isSaving ? 'Saving…' : isNew ? 'Create draft' : 'Save'}
                </Button>
              )}

              {status === 'draft' && (
                <Button
                  onClick={onPublish}
                  disabled={isDirty}
                  size="sm"
                  className="bg-teal-600 text-white hover:bg-teal-500"
                >
                  <PublishIcon className="size-4 fill-current" />
                  Publish
                </Button>
              )}

              {hasId && runExperimentButton}
              {hasId && archiveButton}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <LifecycleStepper status={status} />
        {hint && <span className="text-xs text-neutral-500">{hint}</span>}
      </div>
    </div>
  );
}
